import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RealEstateNavbar from "../components/realestateagents/RealEstateNavbar";
import { axiosInstancev1 } from "../lib/axiosInstance";

import { MapPin, Expand, Search } from "lucide-react";
import LocationAutocomplete from "../components/LocationAutocomplete";

function HousingPlotsHero() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [activeTab, setActiveTab] = useState("dtcp");
  const [postedPropertyPlots, setPostedPropertyPlots] = useState([]);
  const [loadingPropertyPlots, setLoadingPropertyPlots] = useState(true);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch posted property plots from builders and agents
  useEffect(() => {
    const fetchPostedPropertyPlots = async () => {
      try {
        setLoadingPropertyPlots(true);
        // Fetch plot properties from search endpoint
        const response = await axiosInstancev1.get("/properties/search", {
          params: {
            property_category: "plot",
            limit: 50,
            page: 1,
          },
        });

        if (response.data.success && response.data.properties) {
          const properties = response.data.properties.map((property) => {
            let imageUrl = property.thumbnail || "/assets/best-deals/apt1.jpg";
            if (imageUrl && imageUrl !== "/assets/best-deals/apt1.jpg") {
              if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
                if (!imageUrl.startsWith("/")) {
                  imageUrl = `/${imageUrl}`;
                }
              }
            }

            const addressParts = [
              property.street_address,
              property.locality,
              property.city,
            ].filter(Boolean);
            const address = addressParts.length > 0 ? addressParts.join(", ") : "Location not specified";

            const propertyTitle = property.plot_title ||
              property.property_title ||
              property.property_type ||
              "Plot";

            const plotDetails = property.plot_details || property.details || {};
            const plotArea = plotDetails.plot_area || property.plot_area || property.built_up_area || property.area || 0;
            const plotAreaUnit = plotDetails.plot_area_unit || property.plot_area_unit || "sqft";
            const plotType = plotDetails.plot_type || property.plot_type || property.property_type || "Plot";
            const facing = property.plot_facing || property.facing_direction || plotDetails.facing_direction || "";
            
            const isDtcp = Boolean(property.dtcp_approved || plotDetails.dtcp_approved);
            const isCmda = Boolean(property.cmda_approved || plotDetails.cmda_approved);
            const isRera = Boolean(property.rera_number || plotDetails.rera_number);
            const approval = isDtcp ? "DTCP Approved" : isCmda ? "CMDA Approved" : isRera ? "RERA Registered" : (plotDetails.approval_status || "");

            return {
              id: property.id,
              title: propertyTitle,
              address: address,
              price: property.expected_price || property.price || property.plot_price || 0,
              listingType: property.listing_type || "Sale",
              area: plotArea,
              areaUnit: plotAreaUnit,
              plotType: plotType,
              facing: facing,
              approval: approval,
              dtcpApproved: isDtcp,
              cmdaApproved: isCmda,
              reraRegistered: isRera,
              image: imageUrl,
              status: "active",
              isFeatured: Boolean(property.is_featured),
              created_at: property.created_at,
            };
          });

          setPostedPropertyPlots(properties);
        }
      } catch (error) {
        console.error("Error fetching posted property plots:", error);
        setPostedPropertyPlots([]);
      } finally {
        setLoadingPropertyPlots(false);
      }
    };

    fetchPostedPropertyPlots();
  }, []);

  // Fetch featured plots
  useEffect(() => {
    const fetchFeaturedProperties = async () => {
      try {
        setLoadingFeatured(true);
        const response = await axiosInstancev1.get("/properties/search", {
          params: {
            property_category: "plot",
            is_featured: true,
            limit: 12,
            page: 1,
          },
        });

        if (response.data.success && response.data.properties) {
          const properties = response.data.properties.map((property) => {
            let imageUrl = property.thumbnail || "/assets/best-deals/apt1.jpg";
            if (imageUrl && imageUrl !== "/assets/best-deals/apt1.jpg") {
              if (!imageUrl.startsWith("http://") && !imageUrl.startsWith("https://")) {
                if (!imageUrl.startsWith("/")) {
                  imageUrl = `/${imageUrl}`;
                }
              }
            }

            const addressParts = [
              property.street_address,
              property.locality,
              property.city,
            ].filter(Boolean);
            const address = addressParts.length > 0 ? addressParts.join(", ") : "Location not specified";

            const propertyTitle = property.plot_title ||
              property.property_title ||
              property.property_type ||
              "Plot";

            const plotDetails = property.plot_details || property.details || {};
            const plotArea = plotDetails.plot_area || property.plot_area || 0;
            const plotAreaUnit = plotDetails.plot_area_unit || property.plot_area_unit || "sqft";
            const plotType = plotDetails.plot_type || property.plot_type || "Plot";
            const facing = property.plot_facing || property.facing_direction || plotDetails.facing_direction || "";
            
            const isDtcp = Boolean(property.dtcp_approved || plotDetails.dtcp_approved);
            const isCmda = Boolean(property.cmda_approved || plotDetails.cmda_approved);
            const isRera = Boolean(property.rera_number || plotDetails.rera_number);
            const approval = isDtcp ? "DTCP Approved" : isCmda ? "CMDA Approved" : isRera ? "RERA Registered" : (plotDetails.approval_status || "");

            return {
              id: property.id,
              title: propertyTitle,
              address: address,
              price: property.expected_price || property.price || property.plot_price || 0,
              listingType: property.listing_type || "Sale",
              area: plotArea,
              areaUnit: plotAreaUnit,
              plotType: plotType,
              facing: facing,
              approval: approval,
              image: imageUrl,
              status: "active",
              isFeatured: true,
            };
          });

          setFeaturedProperties(properties);
        }
      } catch (error) {
        console.error("Error fetching featured properties:", error);
        setFeaturedProperties([]);
      } finally {
        setLoadingFeatured(false);
      }
    };

    fetchFeaturedProperties();
  }, []);

  // Filter helper arrays
  const dtcpPlots = postedPropertyPlots.filter(p => p.dtcpApproved || p.approval.includes("DTCP"));
  const cmdaPlots = postedPropertyPlots.filter(p => p.cmdaApproved || p.approval.includes("CMDA"));
  const reraPlots = postedPropertyPlots.filter(p => p.reraRegistered || p.approval.includes("RERA"));
  const commercialIndustrialPlots = postedPropertyPlots.filter(
    p => p.plotType?.toLowerCase().includes("commercial") || p.plotType?.toLowerCase().includes("industrial")
  );
  
  // All Featured Plots (combining featured endpoint results & featured items from general list)
  const allFeaturedPlots = [...featuredProperties, ...postedPropertyPlots.filter(p => p.isFeatured && !featuredProperties.some(f => f.id === p.id))];

  // Card component helper for consistency across sections
  const renderPlotCard = (property) => (
    <div
      key={property.id}
      className="bg-white rounded-xl md:rounded-2xl overflow-hidden cursor-pointer group hover:shadow-xl transition-shadow duration-300 border border-gray-100 hover:border-gray-200"
      onClick={() => navigate(`/property/${property.id}`)}
    >
      <div className="relative h-32 sm:h-40 md:h-48 overflow-hidden rounded-t-xl md:rounded-t-2xl">
        <img
          src={property.image}
          alt={property.title}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          onError={(e) => { e.target.src = "https://placehold.co/600x400?text=Plot"; }}
        />

        <div className="absolute top-2 left-2 md:top-3 md:left-3 flex gap-1 md:gap-2 z-10 flex-wrap">
          <span className="px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[8px] md:text-[10px] font-bold text-white bg-blue-600 uppercase tracking-wide">
            For {property.listingType || "Sale"}
          </span>

          <span className="px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[8px] md:text-[10px] font-bold text-white bg-black/50 backdrop-blur-md uppercase tracking-wide">
            {property.plotType || "Plot"}
          </span>

          {property.approval && (
            <span className="px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[8px] md:text-[10px] font-bold text-white bg-emerald-600 uppercase tracking-wide">
              {property.approval}
            </span>
          )}
        </div>
      </div>

      <div className="p-2 md:p-3">
        <h3 className="text-xs md:text-sm font-bold text-gray-900 mb-0.5 md:mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
          {property.title}
        </h3>

        <div className="flex items-center text-gray-500 text-[8px] md:text-[10px] mb-1 md:mb-2">
          <MapPin size={10} className="mr-0.5 md:mr-1 text-gray-400 flex-shrink-0" />
          <span className="line-clamp-1">{property.address}</span>
        </div>

        <div className="flex items-center justify-between mb-1 md:mb-2 text-gray-600 text-[8px] md:text-[10px]">
          <div className="flex items-center gap-0.5 md:gap-1">
            <Expand size={10} className="text-gray-400 md:hidden" strokeWidth={2} />
            <Expand size={14} className="text-gray-400 hidden md:block" strokeWidth={2} />
            <span className="font-semibold text-gray-900">{property.area} {property.areaUnit}</span>
          </div>
          {property.facing && (
            <span className="text-[9px] md:text-[11px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
              {property.facing} Facing
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-baseline">
            <span className="text-xs md:text-base font-bold text-[#ff5a3c]">
              ₹{property.price > 0 ? Number(property.price).toLocaleString("en-IN") : "Price on req"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="relative z-[70]">
        <RealEstateNavbar />
      </div>

      {/* HERO SECTION - MATCHING OLD DESIGN EXACTLY */}
      <section className="relative w-full min-h-[480px] sm:min-h-[520px] flex items-center justify-center z-50">
        {/* Background Image */}
        <img
          src="/assets/Property Banner.jpg"
          alt="property banner"
          className="absolute inset-0 w-full h-full object-center"
          draggable={false}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        {/* Content */}
        <div className="relative z-20 w-full flex flex-col items-center px-3 pt-6 sm:pt-10">
          {/* Heading */}
          <h1 className="
            text-lg 
            sm:text-3xl 
            md:text-4xl 
            lg:text-5xl 
            font-semibold 
            text-white 
            text-center 
            leading-snug 
            mb-1 
          ">
            The <span className="text-blue-500 font-bold">#1</span> site real estate
            <br className="hidden sm:block" />
            professionals trust*
          </h1>

          {/* Sub Text */}
          <p className="
            text-[10px] 
            sm:text-sm 
            text-gray-100 
            text-center 
            mb-4 sm:mb-6 
            max-w-xs sm:max-w-md 
          ">
          </p>

          {/* Search Bar */}
          <div className="w-full max-w-[95%] sm:max-w-3xl relative z-[100] mt-4 md:mt-8">
            <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-300">
              {/* TABS ROW: DTCP Approved, CMDA Approved, RERA Registered */}
              <div className="flex items-center px-4 md:px-6 pt-3 md:pt-4 gap-6 md:gap-10 border-b border-gray-100/50 overflow-x-auto no-scrollbar">
                <button
                  onClick={() => setActiveTab("dtcp")}
                  className={`pb-3 text-sm md:text-[15px] font-semibold transition-all duration-300 relative whitespace-nowrap ${activeTab === "dtcp" ? "text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  DTCP Approved
                  {activeTab === "dtcp" && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 rounded-t-full shadow-[0_-2px_6px_rgba(37,99,235,0.4)]"></div>}
                </button>

                <button
                  onClick={() => setActiveTab("cmda")}
                  className={`pb-3 text-sm md:text-[15px] font-semibold transition-all duration-300 relative whitespace-nowrap ${activeTab === "cmda" ? "text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  CMDA Approved
                  {activeTab === "cmda" && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 rounded-t-full shadow-[0_-2px_6px_rgba(37,99,235,0.4)]"></div>}
                </button>

                <button
                  onClick={() => setActiveTab("rera")}
                  className={`pb-3 text-sm md:text-[15px] font-semibold transition-all duration-300 relative whitespace-nowrap ${activeTab === "rera" ? "text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  RERA Registered
                  {activeTab === "rera" && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 rounded-t-full shadow-[0_-2px_6px_rgba(37,99,235,0.4)]"></div>}
                </button>
              </div>

              {/* SEARCH ROW */}
              <div className="flex flex-col md:flex-row items-stretch p-1.5 md:p-2 gap-2">
                <LocationAutocomplete
                  value={keyword}
                  onChange={setKeyword}
                  onSelect={(item) => setKeyword(item.label)}
                  placeholder={
                    activeTab === "dtcp"
                      ? 'Search "DTCP Plot in Chennai"'
                      : activeTab === "cmda"
                        ? 'Search "CMDA Plot in Chennai"'
                        : 'Search "RERA Plot in Tamil Nadu"'
                  }
                  mode="all"
                  className="flex-1"
                  inputWrapperClassName="flex items-center bg-gray-50/80 rounded-2xl border border-gray-100 px-3 md:px-5 py-2 md:py-0 transition-all focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-200"
                  inputClassName="flex-1 bg-transparent py-2.5 md:py-3 outline-none text-gray-800 text-[14px] placeholder-gray-400"
                  renderPrefix={
                    <Search className="text-gray-400 mr-3 hidden sm:block" size={18} />
                  }
                />

                {/* Search Button */}
                <button
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set("type", "plot");
                    if (activeTab === "dtcp") params.set("dtcp", "true");
                    if (activeTab === "cmda") params.set("cmda", "true");
                    if (activeTab === "rera") params.set("rera", "true");
                    if (keyword) params.set("keyword", keyword);
                    navigate(`/properties?${params.toString()}`);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl px-6 md:px-8 py-2.5 md:py-3 shadow-lg shadow-blue-200 transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 flex-shrink-0"
                >
                  <Search size={20} strokeWidth={2.5} />
                  <span className="text-base">Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE OUR PROPERTIES - CATEGORY CARDS */}
      <section className="w-full px-4 py-8 md:py-20 bg-white relative z-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
            Explore Our Properties
          </h2>
          <p className="text-gray-600 text-center text-sm md:text-base mb-6 md:mb-10 leading-relaxed">
            Our Properties of Ducktail
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {/* Residential Plot */}
            <div className="relative group cursor-pointer overflow-hidden rounded-xl h-48 sm:h-64" onClick={() => navigate("/properties?type=plot&plot_type=Residential Plot")}>
              <img
                src="/assets/205.png"
                alt="Residential Plot"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
              <div className="absolute top-6 left-6 text-left z-10">
                <h3 className="font-bold text-white text-sm sm:text-base">Residential Plot</h3>
              </div>
            </div>

            {/* Commercial Plot */}
            <div className="relative group cursor-pointer overflow-hidden rounded-xl h-48 sm:h-64" onClick={() => navigate("/properties?type=plot&plot_type=Commercial Plot")}>
              <img
                src="/assets/209.png"
                alt="Commercial Plot"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
              <div className="absolute top-6 left-6 text-left z-10">
                <h3 className="font-bold text-white text-sm sm:text-base">Commercial Plot</h3>
              </div>
            </div>

            {/* Industrial Plot */}
            <div className="relative group cursor-pointer overflow-hidden rounded-xl h-48 sm:h-64" onClick={() => navigate("/properties?type=plot&plot_type=Industrial Plot")}>
              <img
                src="/assets/208.png"
                alt="Industrial Plot"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
              <div className="absolute top-6 left-6 text-left z-10">
                <h3 className="font-bold text-white text-sm sm:text-base">Industrial Plot</h3>
              </div>
            </div>

            {/* DTCP Approved */}
            <div className="relative group cursor-pointer overflow-hidden rounded-xl h-48 sm:h-64" onClick={() => navigate("/properties?type=plot&dtcp=true")}>
              <img
                src="/assets/206.png"
                alt="DTCP Approved"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
              <div className="absolute top-6 left-6 text-left z-10">
                <h3 className="font-bold text-white text-sm sm:text-base">DTCP Approved</h3>
              </div>
            </div>

            {/* CMDA Approved */}
            <div className="relative group cursor-pointer overflow-hidden rounded-xl h-48 sm:h-64 hidden md:block" onClick={() => navigate("/properties?type=plot&cmda=true")}>
              <img
                src="/assets/207.png"
                alt="CMDA Approved"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>
              <div className="absolute top-6 left-6 text-left z-10">
                <h3 className="font-bold text-white text-sm sm:text-base">CMDA Approved</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR MISSION SECTION */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-lg md:text-2xl lg:text-3xl font-semibold text-gray-900 mb-1 md:mb-2 leading-tight">
              Our mission is to redefine real estate<br className="hidden sm:block" /><span className="sm:hidden"> </span>in the customer's favor.
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Building transparency and trust in every property transaction.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            <div className="bg-gray-50 rounded-lg md:rounded-xl border border-gray-100 p-3 md:p-6 flex flex-col items-start">
              <span className="text-[#EB5757] text-lg md:text-2xl font-bold mb-0.5 md:mb-1">5.2M+</span>
              <span className="text-xs md:text-sm font-semibold text-gray-600 mb-0.5 md:mb-1 leading-tight">Sq.Ft Verified Area</span>
              <span className="text-[10px] md:text-xs text-gray-400 leading-tight hidden sm:block">Premium land parcels vetted for transparency and security.</span>
            </div>
            <div className="bg-gray-50 rounded-lg md:rounded-xl border border-gray-100 p-3 md:p-6 flex flex-col items-start">
              <span className="text-[#EB5757] text-lg md:text-2xl font-bold mb-0.5 md:mb-1">7K+</span>
              <span className="text-xs md:text-sm font-semibold text-gray-600 mb-0.5 md:mb-1 leading-tight">Verified Plots</span>
              <span className="text-[10px] md:text-xs text-gray-400 leading-tight hidden sm:block">Residential and commercial plots available across top cities.</span>
            </div>
            <div className="bg-gray-50 rounded-lg md:rounded-xl border border-gray-100 p-3 md:p-6 flex flex-col items-start">
              <span className="text-[#EB5757] text-lg md:text-2xl font-bold mb-0.5 md:mb-1">4K+</span>
              <span className="text-xs md:text-sm font-semibold text-gray-600 mb-0.5 md:mb-1 leading-tight">Happy Customers</span>
              <span className="text-[10px] md:text-xs text-gray-400 leading-tight hidden sm:block">Trusted by thousands of families in their property journey.</span>
            </div>
            <div className="bg-gray-50 rounded-lg md:rounded-xl border border-gray-100 p-3 md:p-6 flex flex-col items-start">
              <span className="text-[#EB5757] text-lg md:text-2xl font-bold mb-0.5 md:mb-1">200+</span>
              <span className="text-xs md:text-sm font-semibold text-gray-600 mb-0.5 md:mb-1 leading-tight">Daily New Leads</span>
              <span className="text-[10px] md:text-xs text-gray-400 leading-tight hidden sm:block">Active marketplace with high builder and agent engagement.</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PLOTS SECTION - ALL FEATURED TOGETHER */}
      <section className="w-full py-8 md:py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              FEATURED PLOTS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Handpicked premium plot & land opportunities
            </p>
          </div>

          {loadingFeatured && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingFeatured && allFeaturedPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {allFeaturedPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingFeatured && allFeaturedPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-6 text-sm">No featured plots at the moment.</p>
          )}
        </div>
      </section>

      {/* DTCP APPROVED PLOTS SECTION */}
      <section className="w-full py-8 md:py-20 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              DTCP APPROVED PLOTS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Verified Directorate of Town and Country Planning approved layout plots
            </p>
          </div>

          {loadingPropertyPlots && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingPropertyPlots && dtcpPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {dtcpPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingPropertyPlots && dtcpPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-6 text-sm">No DTCP approved plots posted yet.</p>
          )}
        </div>
      </section>

      {/* CMDA APPROVED PLOTS SECTION */}
      <section className="w-full py-8 md:py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              CMDA APPROVED PLOTS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Chennai Metropolitan Development Authority approved land parcels
            </p>
          </div>

          {loadingPropertyPlots && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingPropertyPlots && cmdaPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {cmdaPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingPropertyPlots && cmdaPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-6 text-sm">No CMDA approved plots posted yet.</p>
          )}
        </div>
      </section>

      {/* RERA REGISTERED PLOTS SECTION */}
      <section className="w-full py-8 md:py-20 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              RERA REGISTERED PLOTS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              RERA registered land parcels for safe and transparent investment
            </p>
          </div>

          {loadingPropertyPlots && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingPropertyPlots && reraPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {reraPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingPropertyPlots && reraPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-6 text-sm">No RERA registered plots posted yet.</p>
          )}
        </div>
      </section>

      {/* COMMERCIAL & INDUSTRIAL PLOTS SECTION */}
      <section className="w-full py-8 md:py-20 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              COMMERCIAL & INDUSTRIAL PLOTS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Prime land parcels suitable for commercial complexes, factories & warehouses
            </p>
          </div>

          {loadingPropertyPlots && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingPropertyPlots && commercialIndustrialPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {commercialIndustrialPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingPropertyPlots && commercialIndustrialPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-6 text-sm">No commercial or industrial plots posted yet.</p>
          )}
        </div>
      </section>

      {/* ALL PLOT LISTINGS SECTION */}
      <section className="w-full py-8 md:py-20 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-600 uppercase mb-4">
              ALL PLOT LISTINGS
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Explore plots posted by builders and real estate agents
            </p>
          </div>

          {loadingPropertyPlots && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loadingPropertyPlots && postedPropertyPlots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {postedPropertyPlots.map((property) => renderPlotCard(property))}
            </div>
          )}

          {!loadingPropertyPlots && postedPropertyPlots.length === 0 && (
            <p className="text-center text-gray-400 mt-10">No properties posted yet.</p>
          )}
        </div>
      </section>

      {/* EXPLORE CITIES SECTION */}
      <section className="w-full py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-3xl font-semibold text-gray-900">
              Explore Cities
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Find properties in top cities
            </p>
          </div>

          <div className="flex md:justify-center gap-4 md:gap-6 overflow-x-auto md:overflow-visible pb-4 px-2 no-scrollbar">
            {/* Chennai */}
            <div className="flex flex-col items-center w-40 cursor-pointer" onClick={() => navigate("/properties?type=plot&keyword=Chennai")}>
              <div className="w-40 h-56 overflow-hidden rounded-xl shadow-sm border border-gray-100">
                <img src="/assets/chennai.png" alt="Chennai" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-gray-500 mt-2">2 Properties</p>
              <p className="text-sm font-semibold text-gray-800">Chennai</p>
            </div>

            {/* Coimbatore */}
            <div className="flex flex-col items-center w-40 cursor-pointer" onClick={() => navigate("/properties?type=plot&keyword=Coimbatore")}>
              <div className="w-40 h-56 overflow-hidden rounded-xl shadow-sm border border-gray-100">
                <img src="/assets/coimbatore.png" alt="Coimbatore" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-gray-500 mt-2">1 Property</p>
              <p className="text-sm font-semibold text-gray-800">Coimbatore</p>
            </div>

            {/* Ooty */}
            <div className="flex flex-col items-center w-40 cursor-pointer" onClick={() => navigate("/properties?type=plot&keyword=Ooty")}>
              <div className="w-40 h-56 overflow-hidden rounded-xl shadow-sm border border-gray-100">
                <img src="/assets/ooty.png" alt="Ooty" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-gray-500 mt-2">2 Properties</p>
              <p className="text-sm font-semibold text-gray-800">Ooty</p>
            </div>

            {/* Kanyakumari */}
            <div className="flex flex-col items-center w-40 cursor-pointer" onClick={() => navigate("/properties?type=plot&keyword=Kanyakumari")}>
              <div className="w-40 h-56 overflow-hidden rounded-xl shadow-sm border border-gray-100">
                <img src="/assets/kanyakumari.png" alt="Kanyakumari" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-gray-500 mt-2">3 Properties</p>
              <p className="text-sm font-semibold text-gray-800">Kanyakumari</p>
            </div>

            {/* Trichy */}
            <div className="flex flex-col items-center w-40 cursor-pointer" onClick={() => navigate("/properties?type=plot&keyword=Tiruchy")}>
              <div className="w-40 h-56 overflow-hidden rounded-xl shadow-sm border border-gray-100">
                <img src="/assets/Tiruchy.png" alt="Trichy" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs text-gray-500 mt-2">8 Properties</p>
              <p className="text-sm font-semibold text-gray-800">Trichy</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="w-full py-8 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl md:text-3xl font-semibold text-gray-900">
              How It works? Find Your Perfect Plot
            </h2>
            <p className="text-gray-600 text-sm md:text-base mt-1 leading-relaxed">
              Building your future starts with finding the right foundation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="space-y-6 md:space-y-10 order-2 md:order-1">
              <div className="flex gap-4 md:gap-5">
                <div className="text-blue-600 text-xl md:text-2xl pt-1">
                  <i className="fa-solid fa-hotel"></i>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Browse Verified Listings
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    Explore thousands of handpicked, legally verified plots and lands<br className="hidden md:block" />
                    across prime locations with transparent documentation.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 md:gap-5">
                <div className="text-blue-600 text-xl md:text-2xl pt-1">
                  <i className="fa-solid fa-user-tie"></i>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Connect with Experts
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    Meet our registered real estate agents and builders for expert<br className="hidden md:block" />
                    guidance, detailed site visits, and investment advice.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 md:gap-5">
                <div className="text-blue-600 text-xl md:text-2xl pt-1">
                  <i className="fa-solid fa-key"></i>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Secure Your Investment
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    Finalize documentation with complete transparency and take<br className="hidden md:block" />
                    the first step toward building your dream project.
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <img
                src="/assets/h31.jpg.jpg"
                alt="How it works"
                className="w-full h-48 sm:h-64 md:h-[420px] object-cover rounded-2xl shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DISCOVER SECTION */}
      <section
        className="relative w-full py-24 md:py-32 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/assets/Property.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
          <h2 className="text-xl md:text-3xl font-semibold text-white leading-tight">
            Discover a place you'll<br className="hidden md:block" /> {" "} love to build
          </h2>

          <p className="text-gray-100 mt-2 md:mt-4 text-xs md:text-base">
            Find the foundation for your dream home or commercial venture with our curated land parcels and verified plots.
          </p>

          <button
            onClick={() => {
              const params = new URLSearchParams();
              params.set("type", "plot");
              window.location.href = `/properties?${params.toString()}`;
            }}
            className="mt-8 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full shadow-md transition flex items-center gap-2 mx-auto"
          >
            View Properties
            <i className="fa-solid fa-arrow-right text-xs"></i>
          </button>
        </div>
      </section>

      {/* WHY YOU SHOULD CHOOSE US */}
      <section className="w-full py-8 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 items-center">
          <div className="relative">
            <img
              src="/assets/260.jpg"
              alt="Luxury properties"
              className="w-full md:w-[600px] h-48 sm:h-64 md:h-[420px] object-cover rounded-2xl shadow-md"
            />
          </div>

          <div>
            <h2 className="text-xl md:text-3xl font-semibold text-gray-900 leading-tight mb-3 md:mb-4">
              Why You Should Choose<br className="hidden md:block" /> Ducktail
            </h2>

            <p className="text-xs md:text-sm text-gray-500 mb-6 md:mb-8 leading-relaxed">
              We are on a mission to simplify real estate by focusing on transparency,
              legality, and customer-first service. Our platform connects you with
              verified opportunities that build lasting value.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              <div>
                <h4 className="text-gray-900 font-semibold mb-1 text-sm md:text-base">
                  Verified & Vetted Plots
                </h4>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  We manually verify every listing's documentation, including DTCP/CMDA approvals.
                </p>
              </div>

              <div>
                <h4 className="text-gray-900 font-semibold mb-1 text-base">
                  Expert Network
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Gain access to a trusted network of agents and builders who prioritize your goals.
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                const params = new URLSearchParams();
                params.set("type", "plot");
                window.location.href = `/properties?${params.toString()}`;
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium shadow-md transition flex items-center gap-2"
            >
              Learn More
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default HousingPlotsHero;
